import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Loader2, UserPlus, UserCheck, Users } from "lucide-react";
import { toast } from "sonner";
import api, { avatarSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { DonorBadge } from "../components/DonorBadge";

const SORTS = [["active", "Recently active"], ["popular", "Most followed"], ["newest", "Newest"]];

const MemberCard = ({ m, onFollow }) => (
  <div data-testid={`member-card-${m.username}`} className="group relative rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0D0E12] hover:border-[rgb(var(--kv-glow)/0.5)] hover:shadow-[0_0_24px_-6px_rgb(var(--kv-glow)/0.5)] transition-[border-color,box-shadow]">
    <div className="h-16 bg-gradient-to-br from-[#0066FF]/30 via-[#0F1117] to-[#00E5FF]/15">
      {m.cover_url && <img src={avatarSrc(m.cover_url)} alt="" loading="lazy" className="w-full h-full object-cover opacity-70" />}
    </div>
    <div className="px-4 pb-4 -mt-7">
      <div className="flex items-end justify-between">
        <Link to={`/u/${m.username}`} className="relative">
          {m.avatar_url ? <img src={avatarSrc(m.avatar_url)} alt={m.username} loading="lazy" className="w-14 h-14 rounded-xl object-cover ring-4 ring-[#0D0E12] bg-[#161922]" />
            : <div className="w-14 h-14 rounded-xl ring-4 ring-[#0D0E12] bg-[#0066FF] flex items-center justify-center text-white text-xl font-heading">{m.username[0].toUpperCase()}</div>}
          {m.online && <span title="Active now" className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#31A24C] ring-2 ring-[#0D0E12]" />}
        </Link>
        {!m.is_me && (
          <button data-testid={`member-follow-${m.username}`} onClick={() => onFollow(m)}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${m.is_following ? "border-white/15 text-white hover:border-[#FF2A5F] hover:text-[#FF2A5F]" : "bg-[#0066FF] border-[#0066FF] text-white hover:bg-[#0055CC]"}`}>
            {m.is_following ? <><UserCheck size={13} /> Following</> : <><UserPlus size={13} /> Follow</>}
          </button>
        )}
      </div>
      <Link to={`/u/${m.username}`} className="block mt-2 min-w-0">
        <p className="text-white font-semibold truncate flex items-center gap-1">{m.display_name}<VerifiedBadge show={m.verified} size={15} /><DonorBadge tier={m.donor_tier} size={14} /></p>
        <p className="font-mono text-[11px] text-[#71717A]">@{m.username}</p>
      </Link>
      {m.bio && <p className="text-[#A1A1AA] text-xs mt-2 line-clamp-2">{m.bio}</p>}
      <p className="font-mono text-[10px] uppercase tracking-wider text-[#71717A] mt-3 flex gap-3">
        <span><b className="text-white">{m.followers}</b> followers</span><span><b className="text-white">{m.posts}</b> posts</span>
      </p>
    </div>
  </div>
);

export default function Members() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sort, setSort] = useState("active");
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [items, setItems] = useState([]);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const sentinel = useRef(null);
  const req = useRef(0);

  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 350); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async (reset) => {
    const id = ++req.current;
    setLoading(true);
    try {
      const { data } = await api.get("/members", { params: { sort, q: term || undefined, skip: reset ? 0 : items.length, limit: 40 } });
      if (id !== req.current) return;
      setItems((p) => (reset ? data.items : [...p, ...data.items.filter((d) => !p.some((x) => x.id === d.id))]));
      setMore(data.has_more);
    } catch { if (id === req.current) setMore(false); }
    if (id === req.current) setLoading(false);
  }, [sort, term, items.length]);

  useEffect(() => { setItems([]); setMore(true); load(true); }, [sort, term]); // eslint-disable-line

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting && more && !loading) load(false); }, { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [more, loading, load]);

  const follow = async (m) => {
    if (!user) { toast("Sign in to follow members"); return nav("/login"); }
    setItems((p) => p.map((x) => (x.id === m.id ? { ...x, is_following: !x.is_following } : x)));
    try {
      const { data } = await api.post(`/users/${m.id}/follow`);
      setItems((p) => p.map((x) => (x.id === m.id ? { ...x, is_following: data.following, followers: data.followers } : x)));
    } catch {
      setItems((p) => p.map((x) => (x.id === m.id ? { ...x, is_following: m.is_following } : x)));
      toast.error("Couldn't update follow");
    }
  };

  return (
    <div data-testid="members-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[rgb(var(--kv-glow))] flex items-center gap-2"><Users size={12} /> Community</p>
      <h1 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mt-1 mb-6">Members</h1>
      <div className="flex flex-wrap gap-2 items-center mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input data-testid="members-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name or @username"
            className="w-full bg-[#111] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[rgb(var(--kv-glow)/0.6)]" />
        </div>
        <div className="flex gap-1 bg-[#111] border border-white/10 rounded-lg p-1">
          {SORTS.map(([k, label]) => (
            <button key={k} data-testid={`members-sort-${k}`} onClick={() => setSort(k)}
              className={`text-xs px-3 py-1.5 rounded-md transition-colors ${sort === k ? "bg-[rgb(var(--kv-glow)/0.18)] text-white" : "text-[#A1A1AA] hover:text-white"}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="members-grid">
        {items.map((m) => <MemberCard key={m.id} m={m} onFollow={follow} />)}
      </div>
      {!loading && !items.length && <p data-testid="members-empty" className="text-center text-[#71717A] py-20">No members found{term ? ` for “${term}”` : ""}.</p>}
      <div ref={sentinel} className="flex justify-center py-8">{loading && <Loader2 className="animate-spin text-[rgb(var(--kv-glow))]" />}</div>
    </div>
  );
}
